"use client";

// The host-actions allowlist — and the AI gate, at the level Gal set.
//
// Research pack, 08_VTOOL_COMPOSER_MODEL.md §4, Events row: "`events out`
// (typed payloads) · `hostActions` allowlist (navigate/openInspector/propose-AI)
// … AI-mutating host actions route through the proposal lane only."
//
// ── The gate is the shape of this file, not a comment in it ─────────────────
// DEC-0461, Gal's words on 21.09: «נתחיל עם 1 כאשר המטרה היא 3» — start at
// level 1 (propose only), the destination is level 3 (free writes). Level 1 is
// enforced here by omission: there is no `apply`, no `write`, no `mutate` on
// this interface. A block that wants to change something can only ask, and the
// asking returns a receipt it cannot forge. Widening the gate later means
// adding a method to this type and passing whatever approval that method needs
// — a code change with a diff, not a flag someone flips at runtime.
//
// ── Why `propose` reports "unavailable" instead of pretending ───────────────
// The proposal lane itself (F9 in 16_MVP_VS_NORTH_STAR.md, the store behind
// `draft → pending → approved → applied → undone`) does not exist. Measured,
// not assumed. So the default `propose` accepts nothing and says so, with a
// reason a caller can render. The alternative — resolving successfully into a
// void — is the failure this whole branch exists to avoid: a surface that looks
// like it works because nothing ever says otherwise.

import { createContext, useContext, useMemo } from "react";

/** What a block asks the host to do. Data only — never a function to call. */
export interface VBlockProposal {
  /** Which block is asking. Filled by the wrapper, not by the block. */
  blockId: string;
  /** Machine kind, e.g. "meta.set" or "record.link". Used for routing + audit. */
  kind: string;
  /** One Hebrew sentence a human approver reads before deciding. */
  summary: string;
  /** The change itself, shaped by `kind`. */
  payload: unknown;
}

export type ProposeResult =
  | { status: "pending"; proposalId: string }
  | { status: "unavailable"; reason: "no-proposal-store" | "not-permitted" };

export interface VBlockHostActions {
  /** Move the user somewhere. Read-only as far as data is concerned. */
  navigate?: (href: string) => void;
  /** Open the host's inspector on this block. */
  openInspector?: (blockId: string) => void;
  /**
   * Ask for a change. Never performs one.
   *
   * This is the entire AI surface at gate level 1. It returns a receipt, and
   * the receipt is allowed to say the proposal went nowhere.
   */
  propose: (proposal: VBlockProposal) => Promise<ProposeResult>;
}

/** The default host: navigation and inspection absent, proposals unstored. */
export const NO_PROPOSAL_STORE: VBlockHostActions = {
  propose: async () => ({ status: "unavailable", reason: "no-proposal-store" }),
};

const HostActionsContext = createContext<VBlockHostActions>(NO_PROPOSAL_STORE);

export function VBlockHostActionsProvider({
  actions,
  children,
}: {
  actions?: VBlockHostActions;
  children: React.ReactNode;
}) {
  const value = useMemo(() => actions ?? NO_PROPOSAL_STORE, [actions]);
  return <HostActionsContext.Provider value={value}>{children}</HostActionsContext.Provider>;
}

/**
 * The actions this block is allowed to ask its host for.
 *
 * Never null: a block outside any host still gets the default, whose `propose`
 * honestly reports that nothing received the proposal. A block should render
 * its "ask" affordance based on what came back, not on whether a host exists.
 */
export function useVBlockHostActions(): VBlockHostActions {
  return useContext(HostActionsContext);
}
